import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { adminEquipmentService } from '../services/admin.equipment.service'
import type { EquipmentDocument, EquipmentStatus } from '@/types'
import type { EquipmentFormValues } from '../types'

interface EquipmentFilters {
  searchTerm: string
  status: EquipmentStatus | ''
  category: string
}

const DEFAULT_FILTERS: EquipmentFilters = {
  searchTerm: '',
  status: '',
  category: '',
}

export function useAdminEquipment() {
  const [equipment, setEquipment] = useState<Array<EquipmentDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<EquipmentFilters>(DEFAULT_FILTERS)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminEquipmentService
      .getAllEquipment()
      .then(setEquipment)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load equipment.'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const filtered = useMemo(() => {
    return equipment.filter((item) => {
      if (filters.status && item.status !== filters.status) return false
      if (filters.category && item.category !== filters.category) return false
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase()
        if (
          !item.name.toLowerCase().includes(term) &&
          !item.category.toLowerCase().includes(term) &&
          !(item.manufacturer?.toLowerCase().includes(term)) &&
          !(item.model?.toLowerCase().includes(term))
        ) {
          return false
        }
      }
      return true
    })
  }, [equipment, filters])

  const categories = useMemo(() => {
    const cats = new Set(equipment.map((e) => e.category))
    return Array.from(cats).sort()
  }, [equipment])

  const addItem = useCallback(
    async (data: EquipmentFormValues): Promise<string> => {
      const id = await adminEquipmentService.createEquipment(data)
      refresh()
      return id
    },
    [refresh]
  )

  const updateItem = useCallback(
    async (id: string, data: Partial<EquipmentFormValues>): Promise<void> => {
      await adminEquipmentService.updateEquipment(id, data)
      refresh()
    },
    [refresh]
  )

  const deleteItem = useCallback(
    async (id: string): Promise<void> => {
      await adminEquipmentService.deleteEquipment(id)
      refresh()
    },
    [refresh]
  )

  return {
    equipment,
    filtered,
    categories,
    loading,
    error,
    filters,
    setFilters,
    addItem,
    updateItem,
    deleteItem,
    refresh,
  }
}
